import { useState, useRef, useEffect } from 'react';
import * as React from 'react';
import { useStripe, useElements } from '@stripe/react-stripe-js';
import validateEmail from '@sdk/utils/validation/validateEmail';
import useSlingApi from '@sdk/api/useSlingApi';
import { SUBSCRIPTION_LEVELS, EXTERNAL_PAYMENT_TYPES } from '@sdk/account/constants';
import gettext from '@sdk/lang/gettext';
import { extendBillingInfo, isCreditCardExpired as checkIfCardExpired } from '@sdk/account/utils';
import usePromiseModalState from '@sdk/components/ui/modal/usePromiseModalState';
import getErrorForToast from '@sdk/api/utils/getErrorForToast';
import useToast from '@sdk/common/toast/useToast';
import type { PickerOption } from '@sdk/components/forms/types';
import type { BillingInfoType } from '@sdk/account/api/AccountApi';
import { StripeAddressElementChangeEvent } from '@stripe/stripe-js';

import { useBillingInfo } from '@web/account/billing/BillingInfoContext';
import type { BillingAddress, SubscriptionUtilsType } from '@web/account/types';
import { useStripeUtils } from '@web/account/hooks/stripeUtils.hooks';
import { mapFormValuesToBillingAddress } from '@web/account/utils/mappers';
import { useBillingView } from '@web/account/billing/BillingViewContext/BillingViewContext';

import config from '@angular/config';

type BillingOptionsType = {
  manageSubscriptionOptions: PickerOption[];
  manageSubscriptionOptionClicked: (opt: PickerOption) => void;
  upgradeToBusinessPickerOption: PickerOption;
  downgradeToPremiumPickerOption: PickerOption;
  changeBillingCyclePickerOption: PickerOption;
  cancelSubscriptionPickerOption: PickerOption;
  pauseSubscriptionModalState;
};

export const useEditBillingOptions = ({
  billingInfo,
  areYearlySubscriptionsEnabled,
  updateSubscriptionModalRef,
  downgradeSubscriptionModalRef,
  goToPricing,
}: {
  billingInfo: BillingInfoType;
  areYearlySubscriptionsEnabled: boolean;
  updateSubscriptionModalRef: React.MutableRefObject<any>;
  downgradeSubscriptionModalRef: React.MutableRefObject<any>;
  goToPricing: () => void;
}): BillingOptionsType => {
  const pauseSubscriptionModalState = usePromiseModalState();

  const upgradeToBusinessPickerOption = {
    key: 'upgrade-to-business',
    value: 'upgrade-to-business',
    label: gettext('Upgrade to Business'),
  };

  const downgradeToPremiumPickerOption = {
    key: 'downgrade-to-premium',
    value: 'downgrade-to-premium',
    label: gettext('Downgrade to Premium'),
  };

  const changeBillingCyclePickerOption = {
    key: 'change-billing-cycle',
    value: 'change-billing-cycle',
    label: gettext('Change billing cycle'),
  };

  const cancelSubscriptionPickerOption = {
    key: 'cancel-subscription',
    value: 'cancel-subscription',
    label: gettext('Cancel subscription'),
  };

  const pauseSubscriptionPickerOption = {
    key: 'pause-subscription',
    value: 'pause-subscription',
    label: gettext('Pause subscription'),
  };

  const viewPlansPickerOption = {
    key: 'view-plans',
    value: 'view-plans',
    label: gettext('View plans'),
  };

  const currentOrFormerLevel = billingInfo.subscriptionLevel || billingInfo.formerSubscriptionLevel;

  const manageSubscriptionOptions: PickerOption[] = [
    viewPlansPickerOption,
    ...(currentOrFormerLevel < SUBSCRIPTION_LEVELS.OLD_PREMIUM && !billingInfo.scheduledToResumeDate
      ? [upgradeToBusinessPickerOption]
      : []),
    ...(areYearlySubscriptionsEnabled &&
    !billingInfo.isYearlyPlan &&
    !billingInfo.scheduledToResumeDate &&
    billingInfo.subscriptionStartDate
      ? [changeBillingCyclePickerOption]
      : []),
    ...(config.PAUSE_SUBSCRIPTION_MENU_ITEM_ENABLED &&
    !billingInfo.scheduledToResumeDate &&
    billingInfo.subscriptionStartDate &&
    !billingInfo.isYearlyPlan
      ? [pauseSubscriptionPickerOption]
      : []),
    ...(currentOrFormerLevel === SUBSCRIPTION_LEVELS.BUSINESS && !billingInfo.scheduledToResumeDate
      ? [downgradeToPremiumPickerOption]
      : []),
    ...(billingInfo.subscriptionStartDate ? [cancelSubscriptionPickerOption] : []),
  ];

  const manageSubscriptionOptionClicked = (opt: PickerOption): void => {
    switch (opt.value) {
      case upgradeToBusinessPickerOption.value:
        return void updateSubscriptionModalRef.current.open(SUBSCRIPTION_LEVELS.BUSINESS);
      case cancelSubscriptionPickerOption.value:
        return void downgradeSubscriptionModalRef.current.open();
      case downgradeToPremiumPickerOption.value:
        return void downgradeSubscriptionModalRef.current.open({ downgradeOnly: true });
      case changeBillingCyclePickerOption.value:
        return void updateSubscriptionModalRef.current.open();
      case pauseSubscriptionPickerOption.value:
        return void pauseSubscriptionModalState.getResult();
      case viewPlansPickerOption.value:
        return void goToPricing();
    }
  };

  return {
    manageSubscriptionOptions,
    manageSubscriptionOptionClicked,
    upgradeToBusinessPickerOption,
    downgradeToPremiumPickerOption,
    changeBillingCyclePickerOption,
    cancelSubscriptionPickerOption,
    pauseSubscriptionModalState,
  };
};

type EditBillingProps = {
  subscriptionUtils: SubscriptionUtilsType;
  goToPricing: () => void;
};

export const useEditBilling = ({ subscriptionUtils, goToPricing }: EditBillingProps) => {
  const api = useSlingApi();
  const { showErrorToast, showSuccessToast } = useToast();
  const { billingInfo, onBillingInfoUpdated } = useBillingInfo();
  const updateSubscriptionModalRef = useRef(null);
  const downgradeSubscriptionModalRef = useRef(null);
  const stripe = useStripe();
  const elements = useElements();
  const { authenticateCard, createPaymentMethod } = useStripeUtils();
  const {
    billingAddressFormValues,
    billingInfoContainerRef,
    isAddressValid,
    isBillingInformationEditMode,
    onAddressChange,
    setIsBillingInformationEditMode,
    setIsSubmitted,
  } = useBillingInformationEdit();
  const [detailsText, setDetailsText] = useState(billingInfo.billingDetails);
  const [isDetailsTextDirty, setIsDetailsTextDirty] = useState(false);
  const setBillingDetails = (billingDetails: string) => {
    setDetailsText(billingDetails);
    setIsDetailsTextDirty(true);
  };
  const areYearlySubscriptionsEnabled =
    subscriptionUtils.areYearlySubscriptionsEnabled(billingInfo);

  const [emails, setEmails] = useState(billingInfo.billingEmails);
  const [isCreditCardEditMode, setIsCreditCardEditMode] = useState(false);
  const isExternalPaymentType = billingInfo.paymentType in EXTERNAL_PAYMENT_TYPES;

  const {
    manageSubscriptionOptions,
    manageSubscriptionOptionClicked,
    upgradeToBusinessPickerOption,
    pauseSubscriptionModalState,
  } = useEditBillingOptions({
    billingInfo,
    updateSubscriptionModalRef,
    downgradeSubscriptionModalRef,
    areYearlySubscriptionsEnabled,
    goToPricing,
  });

  const isCreditCardExpired = checkIfCardExpired(billingInfo.creditCard);

  const areBillingEmailsChanged = (): boolean =>
    emails.join('\n') !== billingInfo.billingEmails.join('\n');

  const isBillingChanged =
    areBillingEmailsChanged() ||
    isCreditCardEditMode ||
    isBillingInformationEditMode ||
    isDetailsTextDirty;

  const areChangesValid =
    isBillingChanged &&
    emails.every((email) => !email || validateEmail(email)) &&
    (!isBillingInformationEditMode || isAddressValid);

  const updateOrRepurchase = async (payload: {
    billingEmails?: string[];
    billingAddress?: BillingAddress;
    newPaymentMethodId?: string;
  }) => {
    if (isDetailsTextDirty) {
      const { data } = await api.account.updateBillingDetails(detailsText);
      onBillingInfoUpdated(extendBillingInfo(data));
      setIsDetailsTextDirty(false);
    }

    const isSubscriptionCancellationPeriod = Boolean(
      billingInfo.subscriptionCancelDate && billingInfo.isPremium
    );
    if (billingInfo.subscriptionStartDate || isSubscriptionCancellationPeriod) {
      const { data } = await api.account.updateSubscription(payload);
      showSuccessToast(gettext('Your billing information was updated'));
      return data;
    }

    const { data } = await api.account.purchaseSubscription({
      ...payload,
      subscriptionLevel: billingInfo.formerSubscriptionLevel,
      isYearlyPlan: billingInfo.formerIsYearlyPlan,
    });
    await authenticateCard(data);
    return data;
  };

  const onSaveHandle = async (): Promise<void> => {
    setIsSubmitted(true);

    const wasSuspended = !billingInfo.isPremium;
    let newPaymentMethodId: string;
    if (isCreditCardEditMode) {
      newPaymentMethodId = await createPaymentMethod(
        elements,
        billingInfo.paymentFailedClientSecret,
        isBillingInformationEditMode
      );
    } else if (isBillingInformationEditMode) {
      // if credit card info was edited, createPaymentMethod will take care of submit
      await elements.submit();
    }

    if (!areChangesValid) {
      return Promise.reject('invalid changes');
    }

    const payload = {
      billingEmails: areBillingEmailsChanged()
        ? emails.filter((email) => email && validateEmail(email))
        : undefined,
      billingAddress: isBillingInformationEditMode
        ? mapFormValuesToBillingAddress(billingAddressFormValues)
        : undefined,
      newPaymentMethodId: isCreditCardEditMode ? newPaymentMethodId : undefined,
    };

    try {
      const data = await updateOrRepurchase(payload);
      const updatedBillingInfo = extendBillingInfo(data);

      onBillingInfoUpdated(updatedBillingInfo);
      setEmails(updatedBillingInfo.billingEmails);
      setIsCreditCardEditMode(false);
      setIsBillingInformationEditMode(false);
      setIsSubmitted(false);

      if (wasSuspended) {
        showSuccessToast(gettext('Your subscription has been reactivated'));
      }
    } catch (e) {
      showErrorToast(getErrorForToast(e));
    }
  };

  const onCancelHandle = () => {
    setEmails(billingInfo.billingEmails);
    setIsCreditCardEditMode(false);
    setIsBillingInformationEditMode(false);
    setIsSubmitted(false);
  };

  const onResumeSubscriptionHandle = async () => {
    try {
      const { data } = await api.account.purchaseSubscription({
        subscriptionLevel: billingInfo.pausedSubscriptionLevel,
        isYearlyPlan: billingInfo.isYearlyPlan,
        billingAddress: billingInfo.billingAddress,
      });
      await authenticateCard(data);
      onBillingInfoUpdated(extendBillingInfo(data));
      showSuccessToast(gettext('Subscription resumed'));
    } catch (e) {
      showErrorToast(getErrorForToast(e));
      throw e;
    }
  };

  const retryOrRepurchase = async () => {
    if (!billingInfo.subscriptionStartDate) {
      const { data } = await api.account.purchaseSubscription({
        subscriptionLevel: billingInfo.formerSubscriptionLevel,
        isYearlyPlan: billingInfo.formerIsYearlyPlan,
        billingAddress: billingInfo.billingAddress,
      });
      await authenticateCard(data);
      showSuccessToast(gettext('Your subscription has been reactivated'));
      return data;
    }

    if (billingInfo.paymentFailedClientSecret) {
      await stripe.confirmCardPayment(billingInfo.paymentFailedClientSecret);

      const { data } = await api.account.getBillingInfo();
      return data;
    }

    const { data } = await api.account.retryPayment();
    showSuccessToast(gettext('Payment succeeded'));
    return data;
  };

  const retryPayment = async () => {
    try {
      const data = await retryOrRepurchase();
      onBillingInfoUpdated(extendBillingInfo(data));
    } catch (e) {
      showErrorToast(gettext("Your payment attempt didn't work"));
      throw e;
    }
  };

  return {
    billingInfoContainerRef,
    updateSubscriptionModalRef,
    downgradeSubscriptionModalRef,
    emails,
    setEmails,
    manageSubscriptionOptions,
    manageSubscriptionOptionClicked,
    upgradeToBusinessPickerOption,
    onSaveHandle,
    onCancelHandle,
    isCreditCardEditMode,
    setIsCreditCardEditMode,
    isBillingChanged,
    areChangesValid,
    billingInfo,
    isExternalPaymentType,
    isBillingInformationEditMode,
    onAddressChange,
    setIsBillingInformationEditMode,
    pauseSubscriptionModalState,
    onResumeSubscriptionHandle,
    retryPayment,
    isCreditCardExpired,
    detailsText,
    setBillingDetails,
  };
};

export const useBillingInformationEdit = () => {
  const {
    billingInfoContainerRef,
    isBillingInformationEditMode,
    isSubmitted,
    scrollToBillingInfoEdit,
    setIsBillingInformationEditMode,
    setIsSubmitted,
    shouldShowAddressErrors,
  } = useBillingView();
  const [billingAddressFormValues, setBillingAddressFormValues] =
    useState<StripeAddressElementChangeEvent['value']>(null);
  const [isAddressValid, setIsAddressValid] = useState(false);
  const onAddressChange = (event: StripeAddressElementChangeEvent) => {
    setBillingAddressFormValues(event.value);
    setIsAddressValid(event.complete);
  };

  // when trying to leave locked screen, we are enabling edit mode and showing errors
  useEffect(() => {
    if (shouldShowAddressErrors) {
      setIsBillingInformationEditMode(true);
    }
  }, [shouldShowAddressErrors, setIsBillingInformationEditMode]);

  useEffect(() => {
    if (isBillingInformationEditMode) {
      scrollToBillingInfoEdit();
    }
  }, [isBillingInformationEditMode, scrollToBillingInfoEdit]);

  return {
    billingAddressFormValues,
    billingInfoContainerRef,
    isAddressValid,
    isBillingInformationEditMode,
    isSubmitted,
    onAddressChange,
    setIsBillingInformationEditMode,
    setIsSubmitted,
  };
};

export const useTaxTopTip = () => {
  const [hasTax, setHasTax] = useState(false);

  const onTaxChange = (taxAmount: number) => {
    setHasTax(Boolean(taxAmount));
  };

  return { hasTax, onTaxChange };
};
